import _ from 'lodash';
import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState } from '../../controller';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import {
  Service,
  ServiceLocation,
  ServiceLocationType,
  StaffMember,
} from '@wix/bookings-uou-types';
import { MemoizedViewModalFactory } from '../viewModel';
import { getAvailableServicesByPreset } from '../../../../utils/state/getAvailableServicesByPreset';
import { Preset } from '../../../../types/types';
import { Experiments } from '@wix/yoshi-flow-editor';
import { DO_NOT_CARE_STAFF } from '../../../../constants/constants';

export enum FilterTypes {
  SERVICE = 'SERVICE',
  LOCATION = 'LOCATION',
  STAFF_MEMBER = 'STAFF_MEMBER',
}

export type FilterOption = {
  label: string;
  srOnlyLabel?: string;
  value: string;
  selected: boolean;
  indeterminate?: boolean;
  children?: FilterOption[];
};

export type FilterViewModel = {
  id: FilterTypes;
  label: string;
  options: FilterOption[];
  maxSelectionNumber?: number;
  note?: string;
};

export const memoizedFiltersViewModel: MemoizedViewModalFactory<FilterViewModel> =
  {
    dependencies: {
      state: ['servicesInView', 'filterOptions'],
      settings: [
        'serviceLabel',
        'locationLabel',
        'staffMemberLabel',
        'headerLocationFilterVisibility',
        'headerStaffFilterVisibility',
        'headerServiceFilterVisibility',
        'headerFiltersVisibility',
      ],
    },
  };

type CategoryData = { id: string; name: string; services: Service[] };

function getServiceCategories(servicesInView: Service[]): CategoryData[] {
  const categories: Record<string, CategoryData> = {};
  servicesInView.forEach((service) => {
    const category = categories[service.info.categoryId];
    if (category) {
      category.services.push(service);
    } else {
      categories[service.info.categoryId] = {
        id: service.info.categoryId,
        name: service.info.categoryName,
        services: [service],
      };
    }
  });
  const sortedCategories = Object.values(categories);
  sortedCategories.sort(({ name: firstCategory }, { name: secondCategory }) =>
    firstCategory.localeCompare(secondCategory),
  );
  sortedCategories.forEach(({ services }) =>
    services.sort(({ info: firstService }, { info: secondService }) =>
      firstService.name.localeCompare(secondService.name),
    ),
  );

  return sortedCategories;
}

function getServiceFilterOptions(
  categories: CategoryData[],
  selectedServices: string[],
  context: CalendarContext,
): FilterOption[] {
  const selectedServicesSet = new Set(selectedServices);
  const serviceToFilterOption = (service: Service): FilterOption => ({
    value: service.id,
    label: service.info.name,
    selected: selectedServicesSet.has(service.id),
  });

  if (categories.length === 1) {
    return categories[0].services.map(serviceToFilterOption);
  } else {
    return categories
      .map(({ name, id, services }): FilterOption => {
        const numberOfSelectedCategoryServices = services.filter((service) =>
          selectedServicesSet.has(service.id),
        ).length;

        return {
          value: id,
          label: name,
          srOnlyLabel: context.t(
            'filter.accessibility.service-filter.parent-label',
            { categoryName: name },
          ),
          selected: services.length === numberOfSelectedCategoryServices,
          indeterminate:
            numberOfSelectedCategoryServices > 0 &&
            numberOfSelectedCategoryServices < services.length,
          children: services.map(serviceToFilterOption),
        };
      })
      .flat();
  }
}

function createServiceFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const { getContent, reportError, settingsParams, settings } = context;
  const { servicesInView, filterOptions } = state;

  const isServiceFilterVisible = settings.get(
    settingsParams.headerServiceFilterVisibility,
  );

  try {
    if (isServiceFilterVisible && servicesInView.length > 1) {
      const categories = getServiceCategories(servicesInView);
      const options = getServiceFilterOptions(
        categories,
        filterOptions.SERVICE,
        context,
      );

      return {
        label: getContent({
          settingsParam: settingsParams.serviceLabel,
          translationKey: 'app.settings.defaults.service-label',
        }),
        options,
        id: FilterTypes.SERVICE,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

function createLocationFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const {
    getContent,
    reportError,
    experiments,
    settingsParams,
    settings,
    preset,
  } = context;
  const { filterOptions, servicesInView } = state;

  const isLocationFilterVisible = settings.get(
    settingsParams.headerLocationFilterVisibility,
  );

  try {
    if (!isLocationFilterVisible) {
      return;
    }

    const availableLocations = (
      getFilterOptions({
        servicesInView,
        optionType: 'locations',
        preset,
        experiments,
      }) as ServiceLocation[]
    ).filter(({ type }) => type === ServiceLocationType.OWNER_BUSINESS);

    if (availableLocations.length > 1) {
      return {
        label: getContent({
          settingsParam: settingsParams.locationLabel,
          translationKey: 'app.settings.defaults.location-label',
        }),
        options: availableLocations
          .sort(
            (
              { businessLocation: firstBusinessLocation },
              { businessLocation: secondBusinessLocation },
            ) =>
              (firstBusinessLocation?.name || '').localeCompare(
                secondBusinessLocation?.name || '',
              ),
          )
          .map(({ businessLocation }) => ({
            selected: filterOptions.LOCATION.includes(
              businessLocation?.id as string,
            ),
            label: businessLocation?.name || '',
            value: businessLocation?.id || '',
          })),
        id: FilterTypes.LOCATION,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

function createStaffMemberFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const {
    getContent,
    reportError,
    experiments,
    settingsParams,
    settings,
    preset,
  } = context;
  const { filterOptions, servicesInView } = state;

  const isMaxFilterSelectionsEnabled = experiments.enabled(
    'specs.bookings.calendar.maxFilterSelections',
  );

  const isMultiServicesAppointmentsEnable = experiments.enabled(
    'specs.bookings.multiServiceAppointment',
  );

  const isStaffFilterVisible = settings.get(
    settingsParams.headerStaffFilterVisibility,
  );

  try {
    if (!isStaffFilterVisible) {
      return;
    }
    const isAnyResource =
      isMultiServicesAppointmentsEnable &&
      filterOptions.STAFF_MEMBER.includes(DO_NOT_CARE_STAFF);

    if (isAnyResource) {
      return;
    }

    const availableStaffMembers = getFilterOptions({
      servicesInView,
      optionType: 'staffMembers',
      preset,
      experiments,
    }) as StaffMember[];

    if (availableStaffMembers.length > 1) {
      return {
        label: getContent({
          settingsParam: settingsParams.staffMemberLabel,
          translationKey: 'app.settings.defaults.staff-member-label',
        }),
        options: availableStaffMembers
          .sort(({ name: firstStaffName }, { name: secondStaffName }) =>
            firstStaffName.localeCompare(secondStaffName),
          )
          .map(({ id, name }) => ({
            selected: filterOptions.STAFF_MEMBER.includes(id),
            label: name,
            value: id,
          })),
        id: FilterTypes.STAFF_MEMBER,
        maxSelectionNumber: isMaxFilterSelectionsEnabled ? 5 : undefined,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

export function createFilterViewModels({
  state,
  context,
}: ViewModelFactoryParams<CalendarState, CalendarContext>): FilterViewModel[] {
  const notUndefined = <T>(value: T): value is NonNullable<T> =>
    value !== undefined;

  return [
    createServiceFilterViewModel(state, context),
    createLocationFilterViewModel(state, context),
    createStaffMemberFilterViewModel(state, context),
  ].filter(notUndefined);
}

const getFilterOptions = ({
  servicesInView,
  optionType,
  preset,
  experiments,
}: {
  servicesInView: Service[];
  optionType: Extract<keyof Service, 'staffMembers' | 'locations'>;
  preset: Preset;
  experiments: Experiments;
}) => {
  const services = experiments.enabled(
    'specs.bookings.calendarServicesDropdownRefactor',
  )
    ? servicesInView
    : getAvailableServicesByPreset({
        servicesInView,
        preset,
      });

  return _.uniqWith(_.flatMap(services, optionType), _.isEqual);
};
